import { FC, HTMLAttributes, useEffect, useState } from "react";
import { ICookieRegistrationProps } from "./ICookieRegistrationProps";
import { useAuth } from "../../../../../modules/auth";
import useCookieStore from "../useCookieStore";
import { useLazyGetCookiesDeviceIdQuery } from "./cookieApi";
import { Spinner } from "../../../../widgets";

export interface IRegistrationProps extends ICookieRegistrationProps, HTMLAttributes<HTMLDivElement> { }

const invalidPrompt = 'Your registration is invalid.';
const validPrompt = 'You are successfully Registered on this device.';
const createdPrompt = 'Your registration for this device is initiated and you will be able to mark attendance once';
const notRegistered = 'You are not registered on this device.';
const browserError = 'Device registration for attendance is supported in Chrome.';

export const CookieRegistration: FC<IRegistrationProps> = ({
    className, cookieName, validAfterMins
}) => {
    const { user } = useAuth();
    const { createCookie, getCookie, hasValidCookie, deleteCookie } = useCookieStore(cookieName)
    const [validity, setValidity] = useState<"VALID" | "CREATED" | "INVALID">()
    const [hasCookie, setHasCookie] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [serverIdentifier, setServerIdentifier] = useState<string>('');
    const [getCookiesDeviceId] = useLazyGetCookiesDeviceIdQuery();
    const [isServerIdentifierReady, setIsServerIdentifierReady] = useState<boolean>(false);

    useEffect(() => {
        if (!user || !user.contactId) return;

        getCookiesDeviceId({
            contactId: user.contactId
        }).unwrap()
            .then((result) => {
                if (result) {
                    setServerIdentifier(result.deviceId);
                    setIsServerIdentifierReady(true);
                } else {
                    console.warn("Failed to retrieve server identifier");
                }
            })
            .catch((err) => console.error("Error fetching server identifier:", err));
    }, [getCookiesDeviceId, user]);



    const isChromeBrowser = (): boolean => {
        const userAgent = navigator.userAgent.toLowerCase();
        const isChrome = userAgent.includes("chrome") && !userAgent.includes("edg") && !userAgent.includes("opr");
        const isMobileChrome = userAgent.includes("crios"); // Chrome on iOS uses 'CriOS'
        return isChrome || isMobileChrome;
    };

    useEffect(() => {
        // Validate browser on component mount
        if (!isChromeBrowser()) {
            setErrorMessage(browserError);
        }

        const contactId = user?.userId;
        if (!contactId) return;

        const data = getCookie();
        if (data)
            setHasCookie(true)

        // eslint-disable-next-line
    }, [user])

    useEffect(() => {
        const contactId = user?.userId;
        if (!contactId || !isServerIdentifierReady) return;

        const validateCookie = async () => {
            const result = await hasValidCookie(contactId, validAfterMins, serverIdentifier);
            setValidity(result);
        };

        validateCookie();

        // eslint-disable-next-line
    }, [user, isServerIdentifierReady, serverIdentifier]);

    const registerCookie = (contactId: string) => {
        setLoading(true)
        createCookie(contactId).then(() => {
            setHasCookie(true);
            setValidity("CREATED");
        }).catch(err => {
            console.log("err", err);
        }).finally(() => setLoading(false));

    }

    const handleRegisterCookie = () => {
        const contactId = user?.userId;
        if (!contactId) return;

        registerCookie(contactId);

    }

    const handleResetCookie = () => {
        const contactId = user?.userId;
        if (!contactId) return;

        deleteCookie()
        registerCookie(contactId)
    }

    return <div className={className}>
        {errorMessage ? <div className="alert alert-danger">{errorMessage}</div> :
            <>
                {(hasCookie) && <div className="d-flex gap-1 align-items-center">
                    {(validity === "INVALID") && <>
                        <span>{invalidPrompt}</span>
                        <span>Do you want to</span>
                        <button
                            onClick={handleResetCookie}
                            disabled={loading}
                            className='btn btn-lg btn-primary me-3 btn-sm'>
                            <span className='indicator-label'>
                                {loading && <Spinner className='me-2' show={loading} />}
                                <span>Reset Registration ?</span>
                            </span>
                        </button>
                    </>}

                    {(validity === "VALID") && <>
                        <span>{validPrompt}</span>
                    </>}

                    {(validity === "CREATED") && <>
                        <span>{createdPrompt}</span>
                        <span>'Registered'.</span>
                    </>}
                </div>}


                {(!hasCookie) && <div className="d-flex gap-1 align-items-center">
                    <span>{notRegistered}</span>
                    <span>Do you want to</span>
                    <button
                        onClick={handleRegisterCookie}
                        disabled={loading}
                        className='btn btn-lg btn-primary me-3 btn-sm'>
                        <span className='indicator-label'>
                            {loading && <Spinner className='me-2' show={loading} />}
                            <span>Register ?</span>
                        </span>
                    </button>
                </div>}
            </>}
    </div>
}
